import { useSyncExternalStore } from 'react';

/**
 * The app's light/dark preference. One store shared by every screen, so the
 * sign-in pages, organisation selection and the signed-in layout all agree.
 *
 * Order of precedence: the saved choice (`flickquote-theme`), then the browser's
 * `prefers-color-scheme`. Until someone picks explicitly nothing is written, so
 * the browser default keeps being followed.
 */
const STORAGE_KEY = 'flickquote-theme';

const listeners = new Set();

const readStored = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'dark' || stored === 'light' ? stored : null;
  } catch {
    return null;
  }
};

const browserPrefersDark = () => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
};

export const getIsDark = () => {
  const stored = readStored();
  return stored ? stored === 'dark' : browserPrefersDark();
};

/** Put the `dark` class on <html> to match the current preference. */
export const applyTheme = () => {
  document.documentElement.classList.toggle('dark', getIsDark());
};

export const setDarkMode = (dark) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light');
  } catch {
    /* storage blocked: the choice still applies for this page view */
  }
  document.documentElement.classList.toggle('dark', dark);
  listeners.forEach((l) => l());
};

const subscribe = (listener) => {
  listeners.add(listener);
  const mq = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  const onChange = () => {
    applyTheme();
    listener();
  };
  mq?.addEventListener?.('change', onChange);
  return () => {
    listeners.delete(listener);
    mq?.removeEventListener?.('change', onChange);
  };
};

/**
 * Subscribing applies the theme to <html> too, so any screen that calls this
 * (or renders ThemeToggle) is themed even when no Layout is mounted.
 */
export const useTheme = () => {
  const darkMode = useSyncExternalStore(subscribe, getIsDark, () => false);
  return { darkMode, setDarkMode };
};
